import React, { useState, useEffect, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthWrapper } from './components/Auth/AuthWrapper';
import { AuthUser } from './hooks/useAuth';
import ErrorBoundary from './components/ErrorBoundary';
import ToastContainer from './components/ToastContainer';
import Sidebar from './components/Layout/SidebarNew';
import ContentTabs from './components/Layout/ContentTabs';
import { migrationService } from './services/migrationService';
import DashboardRenderer from './components/Dashboard/DashboardRenderer';
import OpportunityDetail from './components/Opportunities/OpportunityDetail';
// FloatingAgentButton removed - Ask Aideline button disabled
import { useFirebaseData } from './hooks/useFirebaseData';
import { Opportunity, Company, Task, Lead, CustomerCompany } from './types';
import { DocumentUploadPrefill } from './types/documentFile';
import { paymentScheduleService } from './services/paymentScheduleService';
import PaymentScheduleEditorModal from './components/PaymentSchedules/PaymentScheduleEditorModal';
import TaskDebug from './components/Debug/TaskDebug';
import { VoiceAssistant } from './components/Voice/VoiceAssistant';
import WhatsNew from './components/WhatsNew';
import OAuthCallback from './components/GoogleAds/OAuthCallback';
import QuickSelectMenu from './components/QuickSelect/QuickSelectMenu';
import { Menu, X } from 'lucide-react';
import FeedbackModal from './components/Feedback/FeedbackModal';
import EchoBrainTab from './components/EchoBrain/EchoBrainTab';
import AddLeadModal from './components/Leads/AddLeadModal';
import CreateOpportunityModal from './components/Opportunities/CreateOpportunityModal';
import QuickAddTask from './components/Tasks/QuickAddTask';
import { Modal, LoadingState } from './components/shared';
import { APP_VERSION } from './config/version';
import { toastService } from './services/toastService';
import { usersService } from './services/usersService';
import { presenceService } from './services/presenceService';
import {
  LazyAutomatePage,
  LazyBrandBookPage,
  LazyBusinessPlanPage,
  LazyClosingAssistant,
  LazyCompaniesPage,
  LazyCompanyProfilePage,
  LazyCustomersPage,
  LazyCustomerIntelligencePage,
  LazyDocumentsPage,
  LazyEmailsPage,
  LazyEngagementsPage,
  LazyExpensesPage,
  LazyFeedbackPage,
  LazyGuidePage,
  LazySoftwareCompliancePage,
  LazyGoogleAdsSection,
  LazyInsightsPage,
  LazyLeadsList,
  LazyMaterialPage,
  LazyMeetingPrepPage,
  LazyOpportunityList,
  LazyProductsPage,
  LazyPublicAuditView,
  LazyStreamPage,
  LazySettingsPage,
  LazyTasksListModern,
  LazyDevelopmentPage,
  LazyTeamSkillsPage,
  LazyTimeTrackingPage,
  LazyUIShowcase,
} from './lazyAppPages';

// MCP & Agent Mode imports
import { AgentModeOverlay } from './components/Agent/AgentModeOverlay';
import { MCPAdminPanel } from './components/MCP/MCPAdminPanel';
import { initializeMCP } from './mcp';
import './styles/agent-mode.css';

// Permission system
import PermissionGuard from './components/PermissionGuard';
import { PageKey } from './types/permissions';

// Echobot
import { EchobotProvider } from './contexts/EchobotContext';
import EchobotWrapper from './components/Echobot/EchobotWrapper';
import EchobotNotificationContainer from './components/Echobot/EchobotNotificationContainer';

// Theme System
import { ThemeProvider } from './contexts/ThemeContext';

// Demo Mode
import { DemoModeProvider } from './contexts/DemoModeContext';

// Responsive
import { useIsDesktop } from './hooks/useResponsive';

type CurrentView = 'dashboard' | 'guide' | 'quick-select' | 'meeting-prep' | 'leads' | 'tasks' | 'time-tracking' | 'expenses' | 'engagements' | 'team-skills' | 'insights' | 'opportunities' | 'companies' | 'customers' | 'automate' | 'closing-assistant' | 'ai-insights' | 'agent-mode' | 'settings' | 'products' | 'stream' | 'material' | 'ui-showcase' | 'offers' | 'documents' | 'emails' | 'business-plan' | 'brandbook' | 'customer-intelligence' | 'google-ads-overview' | 'google-ads-campaigns' | 'google-ads-optimization' | 'google-ads-assets' | 'google-ads-templates' | 'google-ads-insights' | 'google-ads-merchant' | 'company-profile' | 'feedback' | 'echo-brain' | 'software-compliance' | 'development';

const VALID_CURRENT_VIEWS = new Set<CurrentView>([
  'dashboard', 'guide', 'quick-select', 'meeting-prep', 'leads', 'tasks', 'time-tracking',
  'expenses', 'engagements', 'team-skills', 'insights', 'opportunities', 'companies',
  'customers', 'automate', 'closing-assistant', 'ai-insights', 'agent-mode', 'settings',
  'products', 'stream', 'material', 'ui-showcase', 'offers', 'documents', 'emails',
  'business-plan', 'brandbook', 'customer-intelligence', 'google-ads-overview',
  'google-ads-campaigns', 'google-ads-optimization', 'google-ads-assets',
  'google-ads-templates', 'google-ads-insights', 'google-ads-merchant',
  'company-profile', 'feedback', 'echo-brain', 'software-compliance', 'development',
]);

function toCurrentView(value: string | null | undefined): CurrentView | null {
  return value && VALID_CURRENT_VIEWS.has(value as CurrentView) ? (value as CurrentView) : null;
}

interface AppContentProps {
  user: AuthUser;
}

const AppContent: React.FC<AppContentProps> = ({ user }) => {
  const { t } = useTranslation('common');
  const pageSuspenseFallback = (
    <div className="flex min-h-[40vh] items-center justify-center">
      <LoadingState text={t('loading')} />
    </div>
  );
  const pageErrorFallback = (
    <div className="mx-auto mt-10 max-w-xl rounded-xl border border-danger bg-bg-card p-6 shadow-card">
      <h2 className="text-lg font-bold text-txt">Tab konnte nicht geladen werden</h2>
      <p className="mt-2 text-sm text-txt-secondary">
        Beim Laden dieser Ansicht ist ein Fehler aufgetreten. Wechseln Sie kurz auf einen anderen Tab
        oder laden Sie die Seite neu, falls es weiterhin passiert.
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setCurrentView('dashboard')}
          className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-txt-inverted hover:opacity-90"
        >
          Dashboard öffnen
        </button>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-lg border border-default bg-bg-card px-4 py-2 text-sm font-medium text-txt hover:bg-bg-surface"
        >
          Seite neu laden
        </button>
      </div>
    </div>
  );
  const [currentView, setCurrentView] = useState<CurrentView>(() => {
    const hash = window.location.hash.slice(1);
    const hashView = toCurrentView(hash);
    if (hashView) return hashView;
    const saved = localStorage.getItem('current-view');
    return toCurrentView(saved) || 'dashboard';
  });
  const [previousView, setPreviousView] = useState<CurrentView>('dashboard');
  const [viewParams, setViewParams] = useState<any>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState<boolean>(() => {
    return localStorage.getItem('sidebar-expanded') === 'true';
  });
  const [dashboardView, setDashboardView] = useState<'user' | 'team'>(() => {
    try { return (localStorage.getItem('dashboard-view-mode') as 'user' | 'team') || 'user'; } catch { return 'user'; }
  });
  const saveDashboardView = (v: 'user' | 'team') => {
    setDashboardView(v);
    try { localStorage.setItem('dashboard-view-mode', v); } catch {}
  };
  const [selectedOpportunity, setSelectedOpportunity] = useState<Opportunity | null>(null);
  const [opportunityForOffer, setOpportunityForOffer] = useState<Opportunity | null>(null);
  const [documentUploadContext, setDocumentUploadContext] = useState<DocumentUploadPrefill | null>(null);
  const [meetingPrepLead, setMeetingPrepLead] = useState<Lead | null>(null);
  const [isVoiceAssistantOpen, setIsVoiceAssistantOpen] = useState<boolean>(false);
  const [showAddLeadModal, setShowAddLeadModal] = useState<boolean>(false);
  const [showAddTaskModal, setShowAddTaskModal] = useState<boolean>(false);
  const [showAddOpportunityModal, setShowAddOpportunityModal] = useState<boolean>(false);
  const [showWhatsNew, setShowWhatsNew] = useState<boolean>(false);
  
  // Payment Schedule Editor State
  const [showPaymentScheduleEditor, setShowPaymentScheduleEditor] = useState<boolean>(false);
  const [paymentScheduleOpportunity, setPaymentScheduleOpportunity] = useState<Opportunity | null>(null);
  const [paymentScheduleCustomer, setPaymentScheduleCustomer] = useState<CustomerCompany | null>(null);
  
  // AI Deep Link State - for navigating to specific items from AI suggestions
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [selectedContact, setSelectedContact] = useState<Lead | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  
  // Feedback Modal State
  const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(false);

  // Agent Mode & MCP State
  const [isAgentModeOpen, setIsAgentModeOpen] = useState<boolean>(false);
  const [isMCPAdminOpen, setIsMCPAdminOpen] = useState<boolean>(false);
  const [mcpInitialized, setMcpInitialized] = useState<boolean>(false);
  const [showOAuthCallback, setShowOAuthCallback] = useState<boolean>(false);

  // Version key for What's New - update this when you want to show the popup again
  const WHATS_NEW_VERSION = 'v2024-10-02-daily-motivation-radio';
  
  // Check for OAuth callback and invite links on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const inviteId = urlParams.get('invite');
    
    if (code && state) {
      // This is an OAuth callback
      setShowOAuthCallback(true);
    }
    
    if (inviteId) {
      // Handle invite link
      handleInviteLink(inviteId);
    }
  }, []);

  const handleInviteLink = async (inviteId: string) => {
    try {
      // Check if user is logged in
      if (!user?.uid) {
        // Store invite ID for after login
        sessionStorage.setItem('pending_invite', inviteId);
        console.log('Invite link detected, will process after login');
        return;
      }

      // User is logged in, process invite
      await usersService.acceptInvite(inviteId);
      
      // Clear the URL parameter
      const url = new URL(window.location.href);
      url.searchParams.delete('invite');
      window.history.replaceState({}, '', url.toString());
      
      // Reload the page to refresh with new organization
      window.location.reload();
    } catch (error) {
      console.error('Error processing invite:', error);
    }
  };

  // Initialize MCP Server on app start
  useEffect(() => {
    const initMCPServer = async () => {
      try {
        console.log('🚀 Initializing MCP Server...');
        await initializeMCP();
        setMcpInitialized(true);
        console.log('✅ MCP Server initialized successfully');
      } catch (error) {
        console.error('❌ Failed to initialize MCP Server:', error);
        // Don't block app if MCP fails - it's an optional feature
      }
    };

    initMCPServer();
  }, []);

  // Keyboard shortcuts for Agent Mode and MCP Admin (Admin only)
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Only allow for admins
      if (user?.profile?.role !== 'admin') return;
      
      // Ctrl + Shift + A = Agent Mode
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        setIsAgentModeOpen(true);
      }
      
      // Ctrl + Shift + M = MCP Admin Panel
      if (e.ctrlKey && e.shiftKey && e.key === 'M') {
        e.preventDefault();
        setIsMCPAdminOpen(true);
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [user?.profile?.role]);

  useEffect(() => {
    window.location.hash = currentView;
  }, [currentView]);

  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash.slice(1);
      const view = toCurrentView(hash);
      if (view) setCurrentView(view);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Check for pending invites after user logs in
  useEffect(() => {
    const checkPendingInvite = async () => {
      if (!user?.uid) return;
      
      const pendingInvite = sessionStorage.getItem('pending_invite');
      if (pendingInvite) {
        sessionStorage.removeItem('pending_invite');
        await handleInviteLink(pendingInvite);
      }
    };
    
    checkPendingInvite();
  }, [user?.uid]);

  // Check if user should see What's New popup
  useEffect(() => {
    const hasSeenVersion = localStorage.getItem(`whatsNew_${WHATS_NEW_VERSION}`);
    if (!hasSeenVersion) {
      // Show after a short delay for better UX
      setTimeout(() => {
        setShowWhatsNew(true);
      }, 1000);
    }
  }, [WHATS_NEW_VERSION]);

  const handleCloseWhatsNew = () => {
    localStorage.setItem(`whatsNew_${WHATS_NEW_VERSION}`, 'true');
    setShowWhatsNew(false);
  };
  
  // Responsive: auto-manage sidebar state
  const isDesktop = useIsDesktop();
  useEffect(() => {
    setIsSidebarOpen(isDesktop);
  }, [isDesktop]);

  // Persist sidebar expanded state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebar-expanded', String(isSidebarExpanded));
  }, [isSidebarExpanded]);

  // Persist current view to localStorage
  useEffect(() => {
    localStorage.setItem('current-view', currentView);
  }, [currentView]);

  // Handle Voice Assistant menu clicks
  useEffect(() => {
    if (currentView === 'ai-insights' || currentView === 'agent-mode') {
      setIsVoiceAssistantOpen(true);
      // Reset to previous view immediately
      setTimeout(() => {
        setCurrentView(previousView);
      }, 0);
    } else {
      // Update previous view for regular navigation
      setPreviousView(currentView);
    }
  }, [currentView, previousView]);

  // Clear opportunity for offer when leaving offers view
  useEffect(() => {
    if (currentView !== 'documents' && currentView !== 'offers') {
      setOpportunityForOffer(null);
    }
  }, [currentView]);

  // Callback for when opportunity is won - opens payment schedule editor
  const handleOpportunityWon = (opportunity: Opportunity, customer: CustomerCompany) => {
    setPaymentScheduleOpportunity(opportunity);
    setPaymentScheduleCustomer(customer);
    setShowPaymentScheduleEditor(true);
  };

  // Save payment schedule to customer
  const handleSavePaymentSchedule = async (schedule: any) => {
    if (!paymentScheduleCustomer) return;
    
    try {
      await paymentScheduleService.addScheduleToCustomer(paymentScheduleCustomer.id, schedule);
      console.log('Payment schedule saved successfully');
    } catch (error) {
      console.error('Error saving payment schedule:', error);
      throw error; // Re-throw to let modal handle it
    }
  };

  const {
    leads,
    tasks,
    opportunities,
    loading,
    createLead,
    updateLead,
    updateLeadStage,
    deleteLead,
    createTask,
    toggleTask,
    assignTask,
    updateTaskFields,
    deleteTask,
    createOpportunity,
    updateOpportunityStage,
    updateOpportunity,
    addLeadToOpportunity
  } = useFirebaseData(user, handleOpportunityWon);

  useEffect(() => {
    if (!user?.uid) return;

    const orgId = user.profile?.orgId || user.uid;
    const displayName = user.profile?.name || user.displayName || user.email || 'User';
    const avatar = user.profile?.avatar || user.photoURL || undefined;

    presenceService.startPresenceTracking(user.uid, orgId, displayName, avatar);

    return () => {
      presenceService.stopPresenceTracking(user.uid, orgId);
    };
  }, [user?.uid, user?.profile?.orgId, user?.profile?.name, user?.profile?.avatar, user?.displayName, user?.email, user?.photoURL]);

  useEffect(() => {
    if (!user?.uid) return;
    const orgId = user.profile?.orgId || user.uid;
    presenceService.updateCurrentPage(user.uid, orgId, currentView);
  }, [currentView, user?.uid, user?.profile?.orgId]);

  /** Täglich max. ein Hinweis: eigene offene Tasks mit Fälligkeit heute oder überfällig (Ticket: automatische Erinnerung). */
  useEffect(() => {
    if (loading || !user?.uid || !tasks?.length) return;

    const dayKey = new Date().toISOString().slice(0, 10);
    const storageKey = `taskDueReminder:${user.uid}:${dayKey}`;
    try {
      if (localStorage.getItem(storageKey)) return;
    } catch {
      return;
    }

    const uid = user.uid;
    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);

    const dueTodayOrOverdue = tasks.filter((task) => {
      if (task.completed) return false;
      if (task.assignedTo !== uid && task.createdBy !== uid) return false;
      if (!task.dueDate) return false;
      const d = task.dueDate instanceof Date ? task.dueDate : new Date(task.dueDate);
      if (Number.isNaN(d.getTime())) return false;
      return d.getTime() <= endOfToday.getTime();
    });

    if (dueTodayOrOverdue.length === 0) return;

    try {
      localStorage.setItem(storageKey, '1');
    } catch {
      /* ignore quota */
    }

    toastService.warning(t('taskDueReminder', { count: dueTodayOrOverdue.length }), 10000);
  }, [loading, user?.uid, tasks, t]);
  
  // Temporarily use empty array for companies until we load them from Firebase
  const companies: Company[] = [];
  
  // Expose migration functions to browser console for immediate use (DEV ONLY)
  useEffect(() => {
    if (import.meta.env.DEV) {
      (window as any).migrationService = migrationService;
      (window as any).migratePostsNow = () => migrationService.migrateAllPostsToEchomotion();
      console.log('📝 Posts migration available! Run migratePostsNow() in console to migrate all posts to org_echomotion_001');
    }
  }, []);

  const handleStageChange = (opportunityId: string, newStage: Opportunity['stage']) => {
    updateOpportunityStage(opportunityId, newStage);
  };

  // Navigation helpers for AI deep linking
  const handleNavigateToTask = (taskId: string) => {
    const task = tasks.find(t => t.id === taskId);
    if (task) {
      setSelectedTask(task);
      setCurrentView('tasks');
    }
  };

  useEffect(() => {
    const handleNavigate = (e: any) => {
      if (e.detail) {
        if (typeof e.detail === 'string') {
          const view = toCurrentView(e.detail);
          if (!view) return;
          setCurrentView(view);
          setViewParams(null);
        } else if (typeof e.detail === 'object' && e.detail.view) {
          const view = toCurrentView(e.detail.view);
          if (!view) return;
          setCurrentView(view);
          setViewParams(e.detail.params || null);
        }
      }
    };
    window.addEventListener('navigate-to-view', handleNavigate);
    return () => window.removeEventListener('navigate-to-view', handleNavigate);
  }, []);

  const handleNavigateToContact = (contactId: string) => {
    const contact = leads.find(l => l.id === contactId);
    if (contact) {
      setSelectedContact(contact);
      setCurrentView('leads');
    }
  };

  const handleNavigateToOpportunity = (oppId: string) => {
    const opportunity = opportunities.find(o => o.id === oppId);
    if (opportunity) {
      setSelectedOpportunity(opportunity);
      setCurrentView('opportunities');
    }
  };

  const handleNavigateToCompany = (companyId: string) => {
    const company = companies.find(c => c.id === companyId);
    if (company) {
      setSelectedCompany(company);
      setCurrentView('companies');
    }
  };

  const renderContent = () => {
    // The Echobot's "open tasks" prompts should reflect the current user's own
    // open tasks (assigned to or created by them), not the whole organisation.
    const myUid = user?.uid;
    const myOpenTaskCount = tasks.filter(t => !t.completed && (t.assignedTo === myUid || t.createdBy === myUid)).length;
    switch (currentView) {
      case 'dashboard': {
        const uid = user?.uid;
        const dashLeads = dashboardView === 'user' ? leads.filter(l => l.assignedTo === uid) : leads;
        const dashTasks = dashboardView === 'user' ? tasks.filter(t => t.assignedTo === uid || t.createdBy === uid) : tasks;
        const dashOpps = dashboardView === 'user' ? opportunities.filter(o => o.assignedTo === uid) : opportunities;
        return (
          <DashboardRenderer
            user={user}
            leads={leads}
            tasks={tasks}
            opportunities={opportunities}
            dashLeads={dashLeads}
            dashTasks={dashTasks}
            dashOpps={dashOpps}
            dashboardView={dashboardView}
            onSaveDashboardView={saveDashboardView}
            onSetCurrentView={(view) => setCurrentView(view as CurrentView)}
            onAddLead={() => setShowAddLeadModal(true)}
            onAddTask={() => setShowAddTaskModal(true)}
            onAddOpportunity={() => setShowAddOpportunityModal(true)}
            onCreateOffer={() => setCurrentView('offers')}
            onOpenAgentMode={user?.profile?.role === 'admin' ? () => setIsAgentModeOpen(true) : undefined}
            onOpenMCPAdmin={user?.profile?.role === 'admin' ? () => setIsMCPAdminOpen(true) : undefined}
            onSelectContact={setSelectedContact}
            onSelectOpportunity={setSelectedOpportunity}
            onSelectTask={setSelectedTask}
          />
        );
      }
      case 'guide':
        return (
          <LazyGuidePage
            onNavigate={(view) => setCurrentView(view as CurrentView)}
          />
        );
      case 'quick-select':
        return (
          <QuickSelectMenu
            leads={leads}
            companies={companies}
            opportunities={opportunities}
            currentUserId={user?.uid}
            currentUserName={user?.profile?.name || user?.displayName || user?.email || 'User'}
            orgId={user?.profile?.orgId || user?.uid}
            onCreateTask={(leadId, leadName) => {
              setSelectedContact(leads.find(l => l.id === leadId) || null);
              setShowAddTaskModal(true);
            }}
            onCreateOpportunity={(leadId, leadName) => {
              setSelectedContact(leads.find(l => l.id === leadId) || null);
              setShowAddOpportunityModal(true);
            }}
            onAssignCompany={(leadId, leadName) => {
              const lead = leads.find(l => l.id === leadId);
              if (lead) {
                setSelectedContact(lead);
                setCurrentView('leads');
              }
            }}
            onAddNote={(leadId, leadName) => {
              const lead = leads.find(l => l.id === leadId);
              if (lead) {
                setSelectedContact(lead);
                setCurrentView('leads');
              }
            }}
            onMeetingPrep={(leadId, leadName) => {
              const lead = leads.find(l => l.id === leadId);
              if (lead) {
                setMeetingPrepLead(lead);
                setCurrentView('meeting-prep');
              }
            }}
            onNavigateToOpportunity={(opportunity) => {
              setSelectedOpportunity(opportunity);
              setCurrentView('opportunities');
            }}
            onNavigateToCompany={(company) => {
              setSelectedCompany(company);
              setCurrentView('companies');
            }}
            onNavigateToLead={(lead) => {
              setSelectedContact(lead);
              setCurrentView('leads');
            }}
          />
        );
      case 'meeting-prep':
        if (!meetingPrepLead) {
          return (
            <div className="flex items-center justify-center h-64">
              <p className="text-txt-tertiary">Kein Kontakt ausgewählt. Bitte wählen Sie einen Kontakt in der Schnellauswahl.</p>
            </div>
          );
        }
        return (
          <LazyMeetingPrepPage
            lead={meetingPrepLead}
            companies={companies}
            opportunities={opportunities}
            tasks={tasks}
            onBack={() => {
              setMeetingPrepLead(null);
              setCurrentView('quick-select');
            }}
            onNavigateToCompany={(company) => {
              setSelectedCompany(company);
              setCurrentView('companies');
            }}
            onNavigateToOpportunity={(opportunity) => {
              setSelectedOpportunity(opportunity);
              setCurrentView('opportunities');
            }}
            onAddNote={(leadId) => {
              const lead = leads.find(l => l.id === leadId);
              if (lead) {
                setSelectedContact(lead);
                setCurrentView('leads');
              }
            }}
            onCreateTask={(leadId) => {
              const lead = leads.find(l => l.id === leadId);
              if (lead) {
                setSelectedContact(lead);
                setSelectedTask(null); // Ensure no task is pre-selected
                setCurrentView('tasks');
              }
            }}
            onCreateOpportunity={(leadId) => {
              const lead = leads.find(l => l.id === leadId);
              if (lead) {
                setSelectedContact(lead);
                setShowAddOpportunityModal(true);
              }
            }}
            onLinkCompany={(leadId) => {
              const lead = leads.find(l => l.id === leadId);
              if (lead) {
                setSelectedContact(lead);
                setCurrentView('leads');
              }
            }}
          />
        );
      case 'leads':
        return (
          <>
            <LazyLeadsList 
              leads={leads}
              preSelectedContact={selectedContact}
              currentUser={user?.profile}
              orgId={user?.profile?.orgId || user?.uid}
              onCreateLead={createLead}
              onUpdateLead={updateLead}
              onUpdateLeadStage={updateLeadStage}
              onDeleteLead={deleteLead}
            />
            
            {/* Echobot for Leads */}
            <EchobotWrapper
              context="leads"
              userRole={user?.profile?.role || 'user'}
              organizationStats={{
                leadCount: leads.length,
                opportunityCount: opportunities.length,
                campaignCount: 0,
                taskCount: myOpenTaskCount
              }}
              userData={{
                tasks: tasks.slice(0, 20).map(t => ({
                  id: t.id, title: t.title, dueDate: t.dueDate, completed: t.completed, priority: t.priority, type: t.type
                })),
                opportunities: opportunities.slice(0, 20).map(o => ({
                  id: o.id, title: o.title, stage: o.stage, value: o.value, companyName: o.company?.name
                })),
                leads: leads.slice(0, 20).map(l => ({
                  id: l.id, firstName: l.firstName, lastName: l.lastName, company: l.company, stage: l.stage, score: l.score
                }))
              }}
              onActionClick={(action) => {
                if (action === 'open-add-lead-modal') setShowAddLeadModal(true);
                if (action.startsWith('navigate-to-')) {
                  const view = action.replace('navigate-to-', '');
                  setCurrentView(view as CurrentView);
                }
              }}
              showOnMount={true}
              enableProactive={true}
            />
          </>
        );
      case 'tasks':
        return (
          <>
            <LazyTasksListModern 
              tasks={tasks}
              leads={leads}
              opportunities={opportunities}
              currentUserId={user?.uid}
              currentUserRole={user?.profile?.role as 'admin' | 'manager' | 'sales_rep'}
              orgId={user?.profile?.orgId || user?.uid}
              preSelectedTask={selectedTask}
              onCreateTask={createTask}
              onUpdateTask={toggleTask}
              onAssignTask={assignTask}
              onUpdateTaskFields={updateTaskFields}
              onDeleteTask={deleteTask}
            />
            
            {/* Echobot for Tasks */}
            <EchobotWrapper
              context="tasks"
              userRole={user?.profile?.role || 'user'}
              organizationStats={{
                leadCount: leads.length,
                opportunityCount: opportunities.length,
                campaignCount: 0,
                taskCount: tasks.filter(t => !t.completed && (t.assignedTo === user?.uid || t.createdBy === user?.uid)).length,
                overdueTasks: tasks.filter(t => !t.completed && (t.assignedTo === user?.uid || t.createdBy === user?.uid) && new Date(t.dueDate) < new Date()).length,
              }}
              userData={{
                tasks: tasks.filter(t => t.assignedTo === user?.uid || t.createdBy === user?.uid).map(t => ({
                  id: t.id, title: t.title, description: t.description, dueDate: t.dueDate, completed: t.completed, priority: t.priority, type: t.type,
                  leadName: leads.find(l => l.id === t.leadId)?.firstName + ' ' + leads.find(l => l.id === t.leadId)?.lastName
                })),
                opportunities: opportunities.slice(0, 10).map(o => ({
                  id: o.id, title: o.title, stage: o.stage, value: o.value, companyName: o.company?.name
                })),
                leads: leads.slice(0, 10).map(l => ({
                  id: l.id, firstName: l.firstName, lastName: l.lastName, company: l.company, stage: l.stage
                }))
              }}
              onActionClick={(action) => {
                if (action === 'open-add-task-modal') setShowAddTaskModal(true);
                if (action.startsWith('navigate-to-')) {
                  const view = action.replace('navigate-to-', '');
                  setCurrentView(view as CurrentView);
                }
              }}
              showOnMount={true}
              enableProactive={true}
            />
          </>
        );
      case 'insights':
        return (
              <LazyInsightsPage 
                leads={leads}
                opportunities={opportunities}
                tasks={tasks}
                companies={companies}
                currentUserId={user?.uid}
                orgId={user?.profile?.orgId || user?.uid}
                userRole={user?.profile?.role as 'admin' | 'manager' | 'sales_rep'}
                onNavigateToTask={handleNavigateToTask}
                onNavigateToContact={handleNavigateToContact}
                onNavigateToOpportunity={handleNavigateToOpportunity}
                onNavigateToCompany={handleNavigateToCompany}
              />
        );
      case 'opportunities':
        return (
          <>
            <LazyOpportunityList 
              opportunities={opportunities}
              preSelectedOpportunity={selectedOpportunity}
              currentUser={user?.profile}
              orgId={user?.profile?.orgId || user?.uid}
              onCreateOpportunity={createOpportunity}
              onOpportunityClick={setSelectedOpportunity}
            />
            
            {/* Echobot for Opportunities */}
            <EchobotWrapper
              context="opportunities"
              userRole={user?.profile?.role || 'user'}
              organizationStats={{
                leadCount: leads.length,
                opportunityCount: opportunities.length,
                campaignCount: 0,
                taskCount: myOpenTaskCount
              }}
              userData={{
                tasks: tasks.slice(0, 10).map(t => ({
                  id: t.id, title: t.title, dueDate: t.dueDate, completed: t.completed, priority: t.priority, type: t.type
                })),
                opportunities: opportunities.map(o => ({
                  id: o.id, title: o.title, stage: o.stage, value: o.value, companyName: o.company?.name, probability: o.probability, expectedCloseDate: o.expectedCloseDate
                })),
                leads: leads.slice(0, 10).map(l => ({
                  id: l.id, firstName: l.firstName, lastName: l.lastName, company: l.company, stage: l.stage
                }))
              }}
              onActionClick={(action) => {
                if (action === 'open-add-opportunity-modal') setShowAddOpportunityModal(true);
                if (action === 'explain-meddpicc') {
                  // Show MEDDPICC explanation
                  alert('MEDDPICC hilft beim Qualifizieren komplexer Enterprise-Deals:\n\nM - Metrics: Quantifizierter ROI\nE - Economic Buyer: Budget-Entscheider\nD - Decision Criteria: Anforderungen\nD - Decision Process: Entscheidungsablauf\nP - Paper Process: Vertragsprozess\nI - Identify Pain: Geschäftsproblem\nC - Champion: Interner Fürsprecher\nC - Competition: Wettbewerb');
                }
                if (action.startsWith('navigate-to-')) {
                  const view = action.replace('navigate-to-', '');
                  setCurrentView(view as CurrentView);
                }
              }}
              showOnMount={true}
              enableProactive={true}
            />
          </>
        );
      case 'companies':
        return (
          <>
            <LazyCompaniesPage
              orgId={user?.profile?.orgId || user?.uid}
              currentUserId={user?.uid}
              currentUser={user?.profile}
              preSelectedCompany={selectedCompany}
              onUploadDocument={(company) => {
                setOpportunityForOffer(null);
                setDocumentUploadContext({ companyId: company.id, companyName: company.name });
                setCurrentView('documents');
              }}
            />
            
            {/* Echobot for Companies */}
            <EchobotWrapper
              context="leads"
              userRole={user?.profile?.role || 'user'}
              organizationStats={{
                leadCount: leads.length,
                opportunityCount: opportunities.length,
                campaignCount: 0,
                taskCount: myOpenTaskCount
              }}
              userData={{
                tasks: tasks.map(t => ({
                  id: t.id, title: t.title, description: t.description, dueDate: t.dueDate, completed: t.completed, priority: t.priority, type: t.type,
                  leadName: leads.find(l => l.id === t.leadId)?.firstName + ' ' + leads.find(l => l.id === t.leadId)?.lastName
                })),
                opportunities: opportunities.map(o => ({
                  id: o.id, title: o.title, stage: o.stage, value: o.value, companyName: o.company?.name
                })),
                leads: leads.map(l => ({
                  id: l.id, firstName: l.firstName, lastName: l.lastName, company: l.company, stage: l.stage, score: l.score
                }))
              }}
              onActionClick={(action) => {
                if (action.startsWith('navigate-to-')) {
                  const view = action.replace('navigate-to-', '');
                  setCurrentView(view as CurrentView);
                }
              }}
              showOnMount={true}
              enableProactive={false}
            />
          </>
        );
      case 'customers': {
        const orgId = user?.profile?.orgId || user?.uid;
        return (
          <>
            <LazyCustomersPage orgId={orgId} />
            
            {/* Echobot for Customers */}
            <EchobotWrapper
              context="leads"
              userRole={user?.profile?.role || 'user'}
              organizationStats={{
                leadCount: leads.length,
                opportunityCount: opportunities.length,
                campaignCount: 0,
                taskCount: myOpenTaskCount
              }}
              userData={{
                tasks: tasks.map(t => ({
                  id: t.id, title: t.title, description: t.description, dueDate: t.dueDate, completed: t.completed, priority: t.priority, type: t.type,
                  leadName: leads.find(l => l.id === t.leadId)?.firstName + ' ' + leads.find(l => l.id === t.leadId)?.lastName
                })),
                opportunities: opportunities.map(o => ({
                  id: o.id, title: o.title, stage: o.stage, value: o.value, companyName: o.company?.name
                })),
                leads: leads.map(l => ({
                  id: l.id, firstName: l.firstName, lastName: l.lastName, company: l.company, stage: l.stage, score: l.score
                }))
              }}
              onActionClick={(action) => {
                if (action.startsWith('navigate-to-')) {
                  const view = action.replace('navigate-to-', '');
                  setCurrentView(view as CurrentView);
                }
              }}
              showOnMount={true}
              enableProactive={false}
            />
          </>
        );
      }
      case 'company-profile':
        return (
          <LazyCompanyProfilePage 
            orgId={user?.profile?.orgId || user?.uid} 
            currentUserRole={user?.profile?.role} 
          />
        );
      case 'products':
        return <LazyProductsPage currentUser={user?.profile} orgId={user?.profile?.orgId || user?.uid} />;
      case 'material':
        return (
          <LazyMaterialPage 
            currentUserId={user?.uid}
            currentUserName={user?.profile?.name || user?.displayName || user?.email || 'User'}
            orgId={user?.profile?.orgId || user?.uid}
          />
        );
      case 'stream': {
        const orgId = user?.profile?.orgId || user?.uid;
        const currentUserName = user?.profile?.name || user?.displayName || user?.email || 'User';
        return (
          <>
            <LazyStreamPage
              currentUserId={user?.uid}
              currentUserName={currentUserName}
              currentUserPhoto={user?.photoURL || user?.profile?.avatar}
              orgId={orgId}
              leads={leads}
              opportunities={opportunities}
              companies={companies}
              tasks={tasks}
            />
            
            {/* Echobot for Stream */}
            <EchobotWrapper
              context="stream"
              userRole={user?.profile?.role || 'user'}
              organizationStats={{
                leadCount: leads.length,
                opportunityCount: opportunities.length,
                campaignCount: 0,
                taskCount: myOpenTaskCount
              }}
              userData={{
                tasks: tasks.map(t => ({
                  id: t.id, title: t.title, description: t.description, dueDate: t.dueDate, completed: t.completed, priority: t.priority, type: t.type,
                  leadName: leads.find(l => l.id === t.leadId)?.firstName + ' ' + leads.find(l => l.id === t.leadId)?.lastName
                })),
                opportunities: opportunities.map(o => ({
                  id: o.id, title: o.title, stage: o.stage, value: o.value, companyName: o.company?.name
                })),
                leads: leads.map(l => ({
                  id: l.id, firstName: l.firstName, lastName: l.lastName, company: l.company, stage: l.stage, score: l.score
                }))
              }}
              onActionClick={(action) => {
                if (action.startsWith('navigate-to-')) {
                  const view = action.replace('navigate-to-', '');
                  setCurrentView(view as CurrentView);
                }
              }}
              showOnMount={true}
              enableProactive={false}
            />
          </>
        );
      }
      case 'settings':
        return (
          <>
            <LazySettingsPage currentUserId={user?.uid} />
            
            {/* Echobot for Settings */}
            <EchobotWrapper
              context="settings"
              userRole={user?.profile?.role || 'user'}
              organizationStats={{
                leadCount: leads.length,
                opportunityCount: opportunities.length,
                campaignCount: 0,
                taskCount: myOpenTaskCount
              }}
              userData={{
                tasks: tasks.map(t => ({
                  id: t.id, title: t.title, description: t.description, dueDate: t.dueDate, completed: t.completed, priority: t.priority, type: t.type,
                  leadName: leads.find(l => l.id === t.leadId)?.firstName + ' ' + leads.find(l => l.id === t.leadId)?.lastName
                })),
                opportunities: opportunities.map(o => ({
                  id: o.id, title: o.title, stage: o.stage, value: o.value, companyName: o.company?.name
                })),
                leads: leads.map(l => ({
                  id: l.id, firstName: l.firstName, lastName: l.lastName, company: l.company, stage: l.stage, score: l.score
                }))
              }}
              onActionClick={(action) => {
                if (action.startsWith('navigate-to-')) {
                  const view = action.replace('navigate-to-', '');
                  setCurrentView(view as CurrentView);
                }
              }}
              showOnMount={true}
              enableProactive={false}
            />
          </>
        );
      case 'ui-showcase':
        return <LazyUIShowcase />;
      case 'documents':
      case 'offers': // backwards compatibility
        return (
          <>
            <LazyDocumentsPage
              orgId={user?.profile?.orgId || user?.uid}
              currentUserId={user?.uid}
              currentUserName={user?.profile?.name || user?.displayName || user?.email || 'User'}
              fromOpportunity={opportunityForOffer || undefined}
              uploadContext={documentUploadContext || undefined}
            />
            
            {/* Echobot for Documents */}
            <EchobotWrapper
              context="opportunities"
              userRole={user?.profile?.role || 'user'}
              organizationStats={{
                leadCount: leads.length,
                opportunityCount: opportunities.length,
                campaignCount: 0,
                taskCount: myOpenTaskCount
              }}
              userData={{
                tasks: tasks.map(t => ({
                  id: t.id, title: t.title, description: t.description, dueDate: t.dueDate, completed: t.completed, priority: t.priority, type: t.type,
                  leadName: leads.find(l => l.id === t.leadId)?.firstName + ' ' + leads.find(l => l.id === t.leadId)?.lastName
                })),
                opportunities: opportunities.map(o => ({
                  id: o.id, title: o.title, stage: o.stage, value: o.value, companyName: o.company?.name
                })),
                leads: leads.map(l => ({
                  id: l.id, firstName: l.firstName, lastName: l.lastName, company: l.company, stage: l.stage, score: l.score
                }))
              }}
              onActionClick={(action) => {
                if (action.startsWith('navigate-to-')) {
                  const view = action.replace('navigate-to-', '');
                  setCurrentView(view as CurrentView);
                }
              }}
              showOnMount={true}
              enableProactive={false}
            />
          </>
        );
      case 'automate':
        return (
          <>
            <LazyAutomatePage 
              leads={leads}
              onSaveAutomation={(leadId, automationData) => {
                // Save automation data to lead
                console.log('Saving automation for lead:', leadId, automationData);
              }}
              onCreateTask={(taskData) => createTask(taskData)}
            />
            
            {/* Echobot for Automate */}
            <EchobotWrapper
              context="opportunities"
              userRole={user?.profile?.role || 'user'}
              organizationStats={{
                leadCount: leads.length,
                opportunityCount: opportunities.length,
                campaignCount: 0,
                taskCount: myOpenTaskCount
              }}
              userData={{
                tasks: tasks.map(t => ({
                  id: t.id, title: t.title, description: t.description, dueDate: t.dueDate, completed: t.completed, priority: t.priority, type: t.type,
                  leadName: leads.find(l => l.id === t.leadId)?.firstName + ' ' + leads.find(l => l.id === t.leadId)?.lastName
                })),
                opportunities: opportunities.map(o => ({
                  id: o.id, title: o.title, stage: o.stage, value: o.value, companyName: o.company?.name
                })),
                leads: leads.map(l => ({
                  id: l.id, firstName: l.firstName, lastName: l.lastName, company: l.company, stage: l.stage, score: l.score
                }))
              }}
              onActionClick={(action) => {
                if (action === 'explain-meddpicc') {
                  alert('MEDDPICC hilft beim Qualifizieren komplexer Enterprise-Deals:\n\nM - Metrics: Quantifizierter ROI\nE - Economic Buyer: Budget-Entscheider\nD - Decision Criteria: Anforderungen\nD - Decision Process: Entscheidungsablauf\nP - Paper Process: Vertragsprozess\nI - Identify Pain: Geschäftsproblem\nC - Champion: Interner Fürsprecher\nC - Competition: Wettbewerb');
                }
                if (action.startsWith('navigate-to-')) {
                  const view = action.replace('navigate-to-', '');
                  setCurrentView(view as CurrentView);
                }
              }}
              showOnMount={true}
              enableProactive={true}
            />
          </>
        );
      case 'closing-assistant':
        return (
          <>
            <LazyClosingAssistant
              opportunities={opportunities}
              onNavigate={(view) => setCurrentView(view as CurrentView)}
            />
            
            {/* Echobot for Closing Assistant */}
            <EchobotWrapper
              context="opportunities"
              userRole={user?.profile?.role || 'user'}
              organizationStats={{
                leadCount: leads.length,
                opportunityCount: opportunities.length,
                campaignCount: 0,
                taskCount: myOpenTaskCount
              }}
              userData={{
                tasks: tasks.map(t => ({
                  id: t.id, title: t.title, description: t.description, dueDate: t.dueDate, completed: t.completed, priority: t.priority, type: t.type,
                  leadName: leads.find(l => l.id === t.leadId)?.firstName + ' ' + leads.find(l => l.id === t.leadId)?.lastName
                })),
                opportunities: opportunities.map(o => ({
                  id: o.id, title: o.title, stage: o.stage, value: o.value, companyName: o.company?.name
                })),
                leads: leads.map(l => ({
                  id: l.id, firstName: l.firstName, lastName: l.lastName, company: l.company, stage: l.stage, score: l.score
                }))
              }}
              onActionClick={(action) => {
                if (action === 'explain-meddpicc') {
                  alert('MEDDPICC hilft beim Qualifizieren komplexer Enterprise-Deals:\n\nM - Metrics: Quantifizierter ROI\nE - Economic Buyer: Budget-Entscheider\nD - Decision Criteria: Anforderungen\nD - Decision Process: Entscheidungsablauf\nP - Paper Process: Vertragsprozess\nI - Identify Pain: Geschäftsproblem\nC - Champion: Interner Fürsprecher\nC - Competition: Wettbewerb');
                }
                if (action.startsWith('navigate-to-')) {
                  const view = action.replace('navigate-to-', '');
                  setCurrentView(view as CurrentView);
                }
              }}
              showOnMount={true}
              enableProactive={true}
            />
          </>
        );
      case 'brandbook':
        return (
          <LazyBrandBookPage
            currentUserId={user?.uid || ''}
            currentUserName={user?.profile?.name || user?.displayName || user?.email || 'User'}
            orgId={user?.profile?.orgId || user?.uid}
          />
        );
      case 'customer-intelligence':
        return (
          <LazyCustomerIntelligencePage
            orgId={user?.profile?.orgId || user?.uid}
            currentUserId={user?.uid || ''}
          />
        );
      case 'emails':
        return (
          <>
            <LazyEmailsPage 
              currentUserId={user?.uid || ''}
              currentUserName={user?.profile?.name || user?.displayName || user?.email || 'User'}
              companies={companies}
              opportunities={opportunities}
              leads={leads}
              tasks={tasks}
              onCreateLead={createLead}
            />
            
            {/* Echobot for Emails */}
            <EchobotWrapper
              context="emails"
              userRole={user?.profile?.role || 'user'}
              organizationStats={{
                leadCount: leads.length,
                opportunityCount: opportunities.length,
                campaignCount: 0,
                taskCount: myOpenTaskCount
              }}
              userData={{
                tasks: tasks.map(t => ({
                  id: t.id, title: t.title, description: t.description, dueDate: t.dueDate, completed: t.completed, priority: t.priority, type: t.type,
                  leadName: leads.find(l => l.id === t.leadId)?.firstName + ' ' + leads.find(l => l.id === t.leadId)?.lastName
                })),
                opportunities: opportunities.map(o => ({
                  id: o.id, title: o.title, stage: o.stage, value: o.value, companyName: o.company?.name
                })),
                leads: leads.map(l => ({
                  id: l.id, firstName: l.firstName, lastName: l.lastName, company: l.company, stage: l.stage, score: l.score
                }))
              }}
              onActionClick={(action) => {
                if (action.startsWith('navigate-to-')) {
                  const view = action.replace('navigate-to-', '');
                  setCurrentView(view as CurrentView);
                }
              }}
              showOnMount={true}
              enableProactive={false}
            />
          </>
        );
      case 'business-plan':
        return (
          <LazyBusinessPlanPage
            orgId={user?.profile?.orgId || user?.uid}
            currentUserId={user?.uid}
          />
        );
      case 'time-tracking':
        return (
          <>
            <PermissionGuard page="time-tracking" user={user?.profile} orgId={user?.profile?.orgId || user?.uid}>
              <LazyTimeTrackingPage
                currentUserId={user?.uid}
                currentUserName={user?.profile?.name || user?.displayName || user?.email || 'User'}
                currentUserRole={user?.profile?.role}
                orgId={user?.profile?.orgId || user?.uid}
                companies={[]}
              />
            </PermissionGuard>
            
            {/* Echobot for Time Tracking */}
            <EchobotWrapper
              context="tasks"
              userRole={user?.profile?.role || 'user'}
              organizationStats={{
                leadCount: leads.length,
                opportunityCount: opportunities.length,
                campaignCount: 0,
                taskCount: myOpenTaskCount
              }}
              userData={{
                tasks: tasks.map(t => ({
                  id: t.id, title: t.title, description: t.description, dueDate: t.dueDate, completed: t.completed, priority: t.priority, type: t.type,
                  leadName: leads.find(l => l.id === t.leadId)?.firstName + ' ' + leads.find(l => l.id === t.leadId)?.lastName
                })),
                opportunities: opportunities.map(o => ({
                  id: o.id, title: o.title, stage: o.stage, value: o.value, companyName: o.company?.name
                })),
                leads: leads.map(l => ({
                  id: l.id, firstName: l.firstName, lastName: l.lastName, company: l.company, stage: l.stage, score: l.score
                }))
              }}
              onActionClick={(action) => {
                if (action.startsWith('navigate-to-')) {
                  const view = action.replace('navigate-to-', '');
                  setCurrentView(view as CurrentView);
                }
              }}
              showOnMount={true}
              enableProactive={false}
            />
          </>
        );
      case 'engagements':
        return (
          <LazyEngagementsPage
            currentUserId={user?.uid}
            currentUserName={user?.profile?.name || user?.displayName || user?.email || 'User'}
            currentUserRole={user?.profile?.role}
            orgId={user?.profile?.orgId || user?.uid}
          />
        );
      case 'expenses':
        return (
          <PermissionGuard page="expenses" user={user?.profile} orgId={user?.profile?.orgId || user?.uid}>
            <LazyExpensesPage
              currentUserId={user?.uid}
              currentUserName={user?.profile?.name || user?.displayName || user?.email || 'User'}
              currentUserRole={user?.profile?.role}
              orgId={user?.profile?.orgId || user?.uid}
            />
          </PermissionGuard>
        );
      case 'team-skills':
        return (
          <LazyTeamSkillsPage
            currentUserId={user?.uid}
            orgId={user?.profile?.orgId || user?.uid}
            isAdmin={user?.profile?.role === 'admin'}
          />
        );
      case 'feedback':
        return (
          <LazyFeedbackPage
            orgId={user?.profile?.orgId || user?.uid}
            currentUserId={user?.uid}
            currentUserName={user?.profile?.name || user?.displayName || user?.email || 'User'}
            currentUserRole={user?.profile?.role}
            onNewTicket={() => setShowFeedbackModal(true)}
          />
        );
      case 'echo-brain':
        return <EchoBrainTab />;
      case 'software-compliance':
        return (
          <LazySoftwareCompliancePage
            orgId={user?.profile?.orgId || user?.uid}
            currentUserId={user?.uid ?? ''}
            isAdmin={user?.profile?.role === 'admin'}
          />
        );
      case 'development':
        return (
          <LazyDevelopmentPage
            tasks={tasks}
            orgId={user?.profile?.orgId || user?.uid}
            currentUserId={user?.uid}
            currentUserName={user?.profile?.name || user?.displayName || user?.email || 'User'}
            currentUserRole={user?.profile?.role as 'admin' | 'manager' | 'sales_rep'}
            onToggleTask={toggleTask}
            onDeleteTask={deleteTask}
            onNavigateToTasks={(devThemeId: string) => {
              try {
                const key = `tasks-filters-${user?.profile?.orgId || user?.uid || 'default'}-devTheme`;
                localStorage.setItem(key, devThemeId);
              } catch {}
              setCurrentView('tasks');
            }}
          />
        );
      case 'google-ads-overview':
      case 'google-ads-campaigns':
      case 'google-ads-optimization':
      case 'google-ads-assets':
      case 'google-ads-templates':
      case 'google-ads-insights':
      case 'google-ads-merchant':
        return (
          <LazyGoogleAdsSection
            orgId={user?.profile?.orgId || user?.uid}
            currentUserId={user?.uid ?? ''}
            currentView={currentView}
            intelligenceId={viewParams?.intelligenceId}
            onNavigate={(view) => setCurrentView(view as CurrentView)}
          />
        );
      case 'ai-insights':
      case 'agent-mode':
        // These cases are handled by useEffect to open the Voice Assistant
        // Should not normally render, but return empty div as fallback
        return <div></div>;
      default:
        return <div>Select a view from the sidebar</div>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-base flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-accent mx-auto"></div>
          <p className="mt-4 text-txt-secondary text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // Show OAuth Callback if we're in the OAuth flow
  if (showOAuthCallback) {
    return <OAuthCallback />;
  }

  return (
    <div className="flex h-screen bg-bg-base overflow-hidden relative z-[1]">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-bg-raised border-b border-default h-14">
        <div className="flex items-center justify-between px-4 h-full">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="w-10 h-10 rounded-pill grid place-items-center text-txt-tertiary hover:text-txt hover:bg-bg-card transition-all active:scale-[0.94]"
            aria-label="Toggle menu"
          >
            {isSidebarOpen ? (
              <X className="w-5 h-5" strokeWidth={1.5} />
            ) : (
              <Menu className="w-5 h-5" strokeWidth={1.5} />
            )}
          </button>
          
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 bg-gradient-to-br from-accent to-info rounded-sm grid place-items-center">
              <span className="text-white font-bold text-sm">B</span>
            </div>
            <h1 className="font-display text-base font-bold text-txt">Business Hub</h1>
          </div>
          
          <div className="w-10" />
        </div>
      </div>

      <Sidebar 
        activeTab={currentView}
        onTabChange={(tab: string) => setCurrentView(tab as CurrentView)}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        currentUserName={user?.profile?.name || user?.displayName || user?.email}
        currentUser={user?.profile}
        orgId={user?.profile?.orgId || user?.uid}
        currentUserId={user?.uid}
        photoURL={user?.photoURL || undefined}
        expanded={isSidebarExpanded}
        onExpandedChange={setIsSidebarExpanded}
        onGiveFeedback={() => setShowFeedbackModal(true)}
      />
      
      <main
        className="flex-1 overflow-y-auto pt-14 lg:pt-0 pb-20 lg:pb-0 h-full transition-[margin-left] duration-300 ease-spring-smooth"
        style={{ marginLeft: isDesktop ? (isSidebarExpanded ? 220 : 68) : 0 }}
      >
        <div className="p-4 sm:px-9 sm:pt-2 sm:pb-20">
          {/* Second-level horizontal tabs for the active navigation group */}
          <ContentTabs
            currentView={currentView}
            onTabChange={(view) => setCurrentView(view as CurrentView)}
            currentUser={user?.profile}
            orgId={user?.profile?.orgId || user?.uid}
          />
          {/* Permission Guard for Page Access */}
          <ErrorBoundary fallback={pageErrorFallback} resetKeys={[currentView]}>
            {(() => {
              // Map currentView to PageKey for permission checking
              // Special views that don't need permission checks
              if (currentView === 'ai-insights' || 
                  currentView === 'agent-mode' || 
                  currentView === 'ui-showcase' ||
                  currentView === 'guide' ||
                  currentView === 'quick-select' || // Quick Select is always accessible
                  currentView === 'meeting-prep' || // Meeting Prep is always accessible
                  currentView === 'company-profile' || // My Company page is always accessible
                  currentView === 'feedback') { // Feedback page is always accessible
                return <Suspense fallback={pageSuspenseFallback}>{renderContent()}</Suspense>;
              }
              
              // Map view to page key
              const pageKey = currentView as PageKey;
              
              return (
                <PermissionGuard
                  user={user?.profile}
                  orgId={user?.profile?.orgId || user?.uid}
                  page={pageKey}
                  requiredLevel="read"
                >
                  <Suspense fallback={pageSuspenseFallback}>{renderContent()}</Suspense>
                </PermissionGuard>
              );
            })()}
          </ErrorBoundary>
        </div>
      </main>

      {/* FloatingAgentButton removed */}

      <VoiceAssistant 
        isOpen={isVoiceAssistantOpen}
        onClose={() => setIsVoiceAssistantOpen(false)}
        tasks={tasks}
        leads={leads}
        opportunities={opportunities}
        companies={companies}
      />

      <OpportunityDetail
        opportunity={selectedOpportunity}
        allLeads={leads}
        isOpen={!!selectedOpportunity}
        onClose={() => setSelectedOpportunity(null)}
        onStageChange={handleStageChange}
        onUpdateOpportunity={updateOpportunity}
        onAddLeadToOpportunity={(oppId: string, leadId: string) => addLeadToOpportunity(oppId, leadId)}
        orgId={user?.profile?.orgId || user?.uid}
        onCreateOffer={(opportunity) => {
          setDocumentUploadContext(null);
          setOpportunityForOffer(opportunity);
          setSelectedOpportunity(null);
          setCurrentView('documents');
        }}
        onUploadDocument={(opportunity) => {
          setOpportunityForOffer(null);
          setDocumentUploadContext({
            opportunityId: opportunity.id,
            opportunityName: opportunity.title,
            companyId: opportunity.company?.id,
            companyName: opportunity.company?.name,
          });
          setSelectedOpportunity(null);
          setCurrentView('documents');
        }}
        currentUserId={user?.uid}
        currentUserName={user?.profile?.name || user?.displayName || user?.email || 'User'}
        currentUserPhoto={user?.photoURL || user?.profile?.avatar}
      />
      
      {/* Task Debug Component - Only show in development */}
      {import.meta.env.DEV && (
        <TaskDebug 
          currentUserId={user?.uid}
          orgId={user?.profile?.orgId || user?.uid}
        />
      )}

      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        currentPage={currentView}
        currentUserId={user?.uid}
        currentUserName={user?.profile?.name || user?.displayName || user?.email}
        currentUserEmail={user?.email || user?.profile?.email}
        currentUserAvatar={user?.photoURL || user?.profile?.avatar}
        orgId={user?.profile?.orgId || user?.uid}
        onOpenTicketOverview={() => {
          setShowFeedbackModal(false);
          setCurrentView('feedback');
        }}
      />

      {/* Quick Action Modals from Dashboard */}
      <AddLeadModal
        isOpen={showAddLeadModal}
        onClose={() => setShowAddLeadModal(false)}
        onAddLead={createLead}
      />
      <CreateOpportunityModal
        isOpen={showAddOpportunityModal}
        onClose={() => setShowAddOpportunityModal(false)}
        onCreateOpportunity={createOpportunity}
      />
      <Modal
        isOpen={showAddTaskModal}
        onClose={() => setShowAddTaskModal(false)}
        title="Aufgabe erstellen"
      >
        <QuickAddTask
          orgId={user?.profile?.orgId || user?.uid || ''}
          currentUserId={user?.uid || ''}
          onTaskCreated={() => setShowAddTaskModal(false)}
        />
      </Modal>

      <ToastContainer />
      <EchobotNotificationContainer />

      {/* What's New Modal - Shows once per version */}
      {showWhatsNew && (
        <WhatsNew onClose={handleCloseWhatsNew} />
      )}

      {/* Agent Mode Overlay - Voice-first CRM interface (Admin only) */}
      {user?.profile?.role === 'admin' && (
        <AgentModeOverlay
          isOpen={isAgentModeOpen}
          onClose={() => setIsAgentModeOpen(false)}
          currentPage={currentView as any}
          contextData={{
            page: currentView as any,
            userRole: user?.profile?.role || 'member',
            organizationStats: {
              leadCount: 0, // These would ideally come from a hook/context
              opportunityCount: 0,
              campaignCount: 0,
              taskCount: 0
            }
          }}
        />
      )}

      {/* MCP Admin Panel - Configuration and testing (Admin only) */}
      {user?.profile?.role === 'admin' && (
        <MCPAdminPanel
          isOpen={isMCPAdminOpen}
          onClose={() => setIsMCPAdminOpen(false)}
        />
      )}

      {/* Payment Schedule Editor Modal */}
      {paymentScheduleOpportunity && paymentScheduleCustomer && (
        <PaymentScheduleEditorModal
          isOpen={showPaymentScheduleEditor}
          onClose={() => {
            setShowPaymentScheduleEditor(false);
            setPaymentScheduleOpportunity(null);
            setPaymentScheduleCustomer(null);
          }}
          opportunity={paymentScheduleOpportunity}
          customer={paymentScheduleCustomer}
          onSave={handleSavePaymentSchedule}
        />
      )}

      {/* Version Badge - Bottom Right Corner */}
      <div className="fixed bottom-3 right-3 z-[10000] pointer-events-none">
        <div className="bg-bg-card/80 backdrop-blur-sm text-txt-muted px-2.5 py-1 rounded-pill text-[10px] font-mono border border-subtle">
          v{APP_VERSION}
        </div>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  // Check if this is a public audit share link (no auth required)
  const pathParts = window.location.pathname.split('/').filter(Boolean);
  if (pathParts[0] === 'share' && pathParts[1] === 'audit' && pathParts[2]) {
    // Public audit view - no auth required
    return (
      <ErrorBoundary>
        <Suspense
          fallback={
            <div className="min-h-screen flex items-center justify-center bg-bg-base">
              <LoadingState text="Laden…" />
            </div>
          }
        >
          <LazyPublicAuditView />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // Normal authenticated app
  return (
    <ErrorBoundary>
      <AuthWrapper>
        {(user) => (
          <ThemeProvider orgId={user?.profile?.orgId || user?.uid || null}>
            <DemoModeProvider>
              <EchobotProvider>
                <AppContent user={user} />
              </EchobotProvider>
            </DemoModeProvider>
          </ThemeProvider>
        )}
      </AuthWrapper>
    </ErrorBoundary>
  );
};

export default App;
